import React, { useRef } from "react";
import { packageMax } from "util/manualDeliveryForm";
import { nextDays, startOptionsFor, windowText } from "util/manualDeliveryTime";

function TextField({ name, label, hint, form, errors, onField, type = "text", inputMode, mono, placeholder, className }) {
  const error = errors[name];
  const inputClasses = ["md-input"];
  if (error) inputClasses.push("md-input--error");
  if (mono) inputClasses.push("md-mono");
  if (className) inputClasses.push(className);
  return (
    <label className="md-field">
      <span>
        {label}
        {hint ? <span className="md-field__hint">{` ${hint}`}</span> : null}
      </span>
      <input
        id={`md-field-${name}`}
        name={name}
        type={type}
        inputMode={inputMode}
        placeholder={placeholder}
        autoComplete="off"
        className={inputClasses.join(" ")}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={error ? `md-error-${name}` : undefined}
        value={form[name]}
        onChange={(event) => onField(name, event.target.value)}
      />
      {error ? (
        <span id={`md-error-${name}`} className="md-field__error">
          {`Error: ${error}`}
        </span>
      ) : null}
    </label>
  );
}

function DayPicker({ days, value, error, onChange }) {
  const refs = useRef([]);
  const index = Math.max(
    0,
    days.findIndex((d) => d.iso === value)
  );
  const move = (to) => {
    const next = (to + days.length) % days.length;
    onChange(days[next].iso);
    if (refs.current[next]) refs.current[next].focus();
  };
  const onKeyDown = (event) => {
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      move(index + 1);
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      move(index - 1);
    }
  };
  return (
    <div className="md-field">
      <span id="md-day-label">Delivery date</span>
      <div
        id="md-field-date"
        role="radiogroup"
        aria-labelledby="md-day-label"
        aria-invalid={error ? "true" : undefined}
        aria-describedby={error ? "md-error-date" : undefined}
        tabIndex={-1}
        className="md-days"
        onKeyDown={onKeyDown}
      >
        {days.map((day, i) => (
          <button
            key={day.iso}
            ref={(element) => {
              refs.current[i] = element;
            }}
            type="button"
            role="radio"
            aria-checked={day.iso === value ? "true" : "false"}
            aria-label={day.long}
            tabIndex={i === index ? 0 : -1}
            className={`md-day${day.iso === value ? " md-day--on" : ""}`}
            onClick={() => onChange(day.iso)}
          >
            <span className="md-day__top">{day.top}</span>
            <span className="md-day__num">{day.num}</span>
          </button>
        ))}
      </div>
      {error ? (
        <span id="md-error-date" className="md-field__error">
          {`Error: ${error}`}
        </span>
      ) : null}
    </div>
  );
}

// Step 1 sections, in the designed order. Package fields are always shown (own driver ignores them) so the form
// never jumps when the carrier changes. Dimensions are not collected yet. carrierMax is the selected courier's own
// package limit, if it has one; the stepper runs 1 to min(12, carrierMax). carriers is the CarrierOptions element.
export default function DeliveryForm({ form, errors, onField, todayIso, now, carriers, showPickupNotes, carrierMax }) {
  const field = { form, errors, onField };
  const starts = startOptionsFor(form.date, now);
  const maxCount = packageMax(carrierMax);
  const clampCount = (count) => Math.min(maxCount, Math.max(1, count));
  return (
    <>
      <fieldset className="md-fieldset">
        <legend className="md-legend">Deliver to</legend>
        <div className="md-grid-auto md-grid-auto--200">
          <TextField {...field} name="first" label="First name" />
          <TextField {...field} name="last" label="Last name" />
          <TextField {...field} name="phone" label="Phone" type="tel" />
          <TextField {...field} name="email" label="Email" type="email" />
        </div>
        <TextField {...field} name="line1" label="Street address" />
        <TextField {...field} name="line2" label="Apartment, unit or buzzer" hint="Optional" />
        <div className="md-grid-auto md-grid-auto--150">
          <TextField {...field} name="city" label="City" />
          <TextField {...field} name="province" label="Province" />
          <TextField {...field} name="postal" label="Postal code" />
        </div>
      </fieldset>

      <fieldset className="md-fieldset">
        <legend className="md-legend">Order</legend>
        <div className="md-field--narrow">
          <TextField {...field} name="value" label="Order total ($)" inputMode="decimal" mono />
        </div>
        <label className="md-field">
          <span>
            Items<span className="md-field__hint"> What's in the delivery. Optional.</span>
          </span>
          <textarea
            id="md-field-items"
            className="md-textarea"
            rows={2}
            value={form.items}
            onChange={(event) => onField("items", event.target.value)}
          />
        </label>
      </fieldset>

      <fieldset className="md-fieldset">
        <legend className="md-legend">When</legend>
        <DayPicker
          days={nextDays(todayIso, 7)}
          value={form.date}
          error={errors.date}
          onChange={(iso) => onField("date", iso)}
        />
        <div className="md-when">
          <label className="md-field">
            <span>Start time</span>
            <select
              id="md-field-start"
              className={`md-select md-select--time${errors.start ? " md-input--error" : ""}`}
              aria-invalid={errors.start ? "true" : undefined}
              aria-describedby={errors.start ? "md-error-start" : undefined}
              value={form.start}
              onChange={(event) => onField("start", event.target.value)}
            >
              {starts.map((option) => (
                <option key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.start ? (
              <span id="md-error-start" className="md-field__error">
                {`Error: ${errors.start}`}
              </span>
            ) : null}
          </label>
          <div className="md-field">
            <span>Delivery window</span>
            <div className="md-readonly">
              {windowText(form.start)}
              <span className="md-muted md-field__hint">{" · 2 hours"}</span>
            </div>
          </div>
        </div>
      </fieldset>

      <fieldset className="md-fieldset">
        <legend className="md-legend">Packages</legend>
        <p className="md-help">Couriers price and label from these. Your own driver ignores them.</p>
        <div className="md-when">
          <div className="md-field">
            <span id="md-count-label">Bags or boxes</span>
            <div className="md-stepper" role="group" aria-labelledby="md-count-label">
              <button
                type="button"
                aria-label="Fewer packages"
                disabled={form.count <= 1}
                onClick={() => onField("count", clampCount(form.count - 1))}
              >
                −
              </button>
              <span aria-live="polite" className="md-mono md-stepper__value">
                {form.count}
              </span>
              <button
                type="button"
                aria-label="More packages"
                disabled={form.count >= maxCount}
                onClick={() => onField("count", clampCount(form.count + 1))}
              >
                +
              </button>
            </div>
          </div>
          <TextField
            {...field}
            name="weight"
            label="Total weight (kg)"
            inputMode="decimal"
            placeholder="Optional"
            mono
            className="md-input--weight"
          />
        </div>
        <div className="md-checks">
          <label className="md-check">
            <input
              type="checkbox"
              checked={form.alcohol}
              onChange={(event) => onField("alcohol", event.target.checked)}
            />
            Contains alcohol
          </label>
          <label className="md-check">
            <input
              type="checkbox"
              checked={form.signature}
              onChange={(event) => onField("signature", event.target.checked)}
            />
            Signature required
          </label>
        </div>
      </fieldset>

      <fieldset className="md-fieldset">
        <legend className="md-legend">Carrier and price</legend>
        <p className="md-help">
          Live estimates for this address, window and packages. The exact price is confirmed on the next step.
        </p>
        {carriers}
        {showPickupNotes ? (
          <TextField {...field} name="pickupNotes" label="Pickup notes" hint="Shown to the courier at the store. Optional." />
        ) : null}
      </fieldset>
    </>
  );
}
